import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSelector } from "react-redux";
import { FaShieldAlt, FaCheckCircle, FaLock } from "react-icons/fa";

const PrivacyPolicy = () => {
  const hotel = useSelector((state) => state.hotel.data);
  const hotelName = hotel?.name || "Ganesh Holiday Homes";
  const email = hotel?.email || "ganeshholidayhomes@gmail.com";
  const contact = hotel?.contact || {
    address: "Narayan Pakhadi Rd, Narayan Pakhadi, Shrivardhan-402110, Maharashtra, India",
    phone: "+91 9673150591",
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Navbar />

      {/* Hero Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #07101e 0%, #0b192c 50%, #1e3e62 100%)",
          color: "#ffffff",
          padding: "50px 0 60px 0",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <div className="container text-center">
          <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-10 text-white px-3 py-1 rounded-pill small fw-semibold mb-3 border border-white border-opacity-15">
            <span>🔒 Data Protection & Security Commitment</span>
          </div>
          <h1 className="h1 fw-bold mb-2 font-heading text-white" style={{ fontSize: "2.5rem" }}>
            Privacy Policy
          </h1>
          <p className="text-white-50 mb-0 mx-auto" style={{ maxWidth: "700px", fontSize: "1.05rem" }}>
            How {hotelName} collects, protects, and handles your personal information.
          </p>
        </div>
      </div>

      {/* Main Section */}
      <main className="container flex-grow-1 py-5">
        <div
          className="mx-auto p-4 p-md-5 rounded-4 shadow-sm bg-white border"
          style={{ maxWidth: "900px" }}
        >
          <div className="d-flex align-items-center gap-2 mb-4 text-danger">
            <FaShieldAlt className="fs-3" />
            <h2 className="h4 fw-bold mb-0 text-dark font-heading">Data Protection & Privacy Commitment</h2>
          </div>

          <p className="text-secondary mb-4" style={{ lineHeight: "1.8", fontSize: "1.02rem" }}>
            We respect your privacy and value your trust. This Privacy Policy explains how <strong>{hotelName}</strong> ("we", "us", or "our") collects, uses, and safeguards personal information when you visit our website (<strong>https://ganeshholidayhomes.com</strong>) or make room reservations for our short-term accommodation in Shrivardhan, Maharashtra.
          </p>

          <hr className="my-4" />

          {/* Section 1: Information We Collect */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">1. Information We Collect</h3>
            <p className="text-secondary mb-2">
              To provide you with seamless room booking and customer support, we collect the following information:
            </p>
            <ul className="list-unstyled ms-2 text-secondary">
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Guest Contact Details:</strong> Full name, 10-digit mobile phone number, and email address.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Reservation Data:</strong> Check-in and check-out dates, selected room category (e.g. Standard AC, Double Family Room), number of guests, and meal preferences.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Payment & Transaction Information:</strong> Advance token transaction references, payment receipt status, and timestamp. (Note: We do not store sensitive credit card numbers or banking passwords on our servers).</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Identity Verification Data:</strong> Government photo ID details presented at physical check-in as mandated by local hotel and law enforcement regulations.</span>
              </li>
            </ul>
          </section>

          <hr className="my-4" />

          {/* Section 2: How We Use Your Information */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">2. Purpose of Data Collection</h3>
            <p className="text-secondary mb-2">
              We collect customer information strictly for the following operational purposes:
            </p>
            <ul className="list-unstyled ms-2 text-secondary">
              <li className="mb-2">• To process, confirm, and manage your room accommodation booking.</li>
              <li className="mb-2">• To communicate reservation vouchers, directions, and front desk check-in instructions via WhatsApp, SMS, or Email.</li>
              <li className="mb-2">• To arrange pre-ordered home-cooked Konkani meals and special room requests.</li>
              <li className="mb-2">• To process eligible refunds in accordance with our Cancellation & Refund Policy.</li>
              <li className="mb-2">• To comply with applicable statutory, tax, and local guest record-keeping laws.</li>
            </ul>
            <p className="text-secondary fw-semibold mt-3">
              We never sell, rent, lease, or trade your personal information with third-party advertisers or marketing agencies.
            </p>
          </section>

          <hr className="my-4" />

          {/* Section 3: Payment Processing & Security */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">3. Secure Payment Processing</h3>
            <p className="text-secondary mb-3">
              Online payments and booking advance deposits on our platform are processed securely via RBI-authorized Payment Aggregators (such as Cashfree Payments / UPI networks).
            </p>
            <div className="p-3 bg-light rounded-4 border d-flex align-items-center gap-3">
              <FaLock className="text-success fs-3 flex-shrink-0" />
              <div className="small text-secondary">
                <strong>SSL Encrypted Transmission:</strong> All communications between your browser and our website are protected by industry-standard 256-bit SSL encryption. Payment gateway partners comply with PCI-DSS data security standards.
              </div>
            </div>
          </section>

          <hr className="my-4" />

          {/* Section 4: Data Retention & User Rights */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">4. Data Retention & Your Rights</h3>
            <p className="text-secondary" style={{ lineHeight: "1.7" }}>
              We retain guest reservation records only for as long as necessary to fulfill booking services and maintain accounting compliance. You have the right to request access to, correction of, or deletion of your contact records from our booking register by contacting our front desk.
            </p>
          </section>

          <hr className="my-4" />

          {/* Section 5: Contact & Grievance */}
          <section className="text-center pt-2">
            <h3 className="h5 fw-bold mb-2 text-dark font-heading">Privacy Inquiries & Grievance Officer</h3>
            <p className="text-secondary small mb-3">
              For any questions regarding our privacy practices or data policies, please reach out directly:
            </p>
            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
              <a
                href={`mailto:${email}`}
                className="btn btn-outline-danger btn-sm rounded-pill px-4 py-2 fw-semibold"
              >
                📧 {email}
              </a>
              <Link
                to="/contact-us"
                className="btn btn-outline-dark btn-sm rounded-pill px-4 py-2 fw-semibold"
              >
                📍 View Contact Us
              </Link>
            </div>
            <p className="text-muted small mt-3 mb-0">
              <strong>{hotelName}</strong> • {contact.address}
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
